/**
 * ExpansionEngine — ONE staged admin workflow (Mar 2026):
 *   1 Upload Seed CSV  →  2 Run Discovery  →  3 Review Discovery
 *   →  4 Run Enrichment  →  5 Review Final  →  6 Export / Import
 *
 * Discovery (Stage 1, /api/admin/discovery/*) resolves each truck's
 * OFFICIAL website/socials with live fetch-verification before the
 * existing enrichment engine (Hunter.io + GPT) ever runs. Cost estimates
 * are shown BEFORE anything runs; a 5-row test mode lets the admin
 * sanity-check a batch first. Hunter only fires on High-confidence or
 * admin-approved rows.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { BACKEND_BASE } from "../../lib/backendBase";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  UploadSimple, DownloadSimple, Trash, Check, X, Globe, MagnifyingGlass,
  InstagramLogo, FacebookLogo, MapPin, ArrowRight, Flask, Rocket,
} from "@phosphor-icons/react";

const STEPS = ["Upload Seed", "Run Discovery", "Review Discovery", "Run Enrichment", "Review Final", "Export / Import"];

const STATUS_CHIPS = [
  { key: "all", label: "All" },
  { key: "resolved", label: "Resolved" },
  { key: "needs_review", label: "Needs Review" },
  { key: "no_match", label: "No Match" },
  { key: "possibly_inactive", label: "Possibly Inactive" },
  { key: "duplicate_possible", label: "Duplicate?" },
  { key: "High", label: "High Conf." },
  { key: "Medium", label: "Medium Conf." },
  { key: "Low", label: "Low Conf." },
];

function ConfPill({ label, score }) {
  const cls = label === "High" ? "bg-emerald-100 text-emerald-800"
    : label === "Medium" ? "bg-amber-100 text-amber-800"
    : "bg-neutral-100 text-neutral-500";
  return (
    <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>
      {label}{score != null ? ` · ${score}` : ""}
    </span>
  );
}

function StatusPill({ s }) {
  const map = {
    resolved: "bg-emerald-100 text-emerald-800",
    needs_review: "bg-amber-100 text-amber-800",
    no_match: "bg-rose-100 text-rose-700",
    possibly_inactive: "bg-neutral-200 text-neutral-600",
    duplicate_possible: "bg-purple-100 text-purple-700",
  };
  return (
    <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${map[s] || "bg-neutral-100 text-neutral-500"}`}>
      {(s || "").replace(/_/g, " ")}
    </span>
  );
}

function UrlCell({ icon: Icon, url, verified }) {
  if (!url) return <span className="text-neutral-300 text-xs">—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer"
       className={`inline-flex items-center gap-1 text-xs hover:underline ${verified ? "text-emerald-700 font-medium" : "text-primary"}`}
       title={url}>
      <Icon className="h-3 w-3 flex-none" />
      <span className="truncate max-w-[140px]">{url.replace(/^https?:\/\/(www\.)?/, "")}</span>
      {verified && <Check weight="bold" className="h-3 w-3 text-emerald-600 flex-none" />}
    </a>
  );
}

function Stepper({ current }) {
  return (
    <ol className="flex flex-wrap gap-2" data-testid="expansion-stepper">
      {STEPS.map((s, i) => {
        const n = i + 1;
        const state = n < current ? "done" : n === current ? "active" : "todo";
        return (
          <li key={s} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border ${
            state === "done" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
            : state === "active" ? "bg-primary text-primary-foreground border-primary"
            : "bg-muted/30 border-border text-muted-foreground"}`}>
            <span>{n}</span> {s}
          </li>
        );
      })}
    </ol>
  );
}

export default function ExpansionEngine() {
  const { user, loading: authLoading } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [data, setData] = useState(null);   // {job, rows, counts, research_job, research_rows}
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try { const r = await api.get("/admin/discovery/jobs"); setJobs(r.data || []); } catch (e) { console.warn("expansion: couldn't load job list", e); }
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const loadJob = useCallback(async (id) => {
    try {
      const r = await api.get(`/admin/discovery/jobs/${id}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load job");
    }
  }, []);

  // Poll while discovery or enrichment is running
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!jobId) return;
    loadJob(jobId);
    pollRef.current = setInterval(() => {
      const st = data?.job?.status;
      const rst = data?.research_job?.status;
      if (st === "discovering" || st === "enriching" || rst === "running" || !data) loadJob(jobId);
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [jobId, data?.job?.status, data?.research_job?.status, loadJob]); // eslint-disable-line

  if (authLoading) return null;
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const job = data?.job;
  const rows = data?.rows || [];
  const counts = data?.counts || {};
  const research = data?.research_job;
  const researchRows = data?.research_rows || [];

  // Current step for the stepper
  const step = !job ? 1
    : job.status === "staged" ? 2
    : job.status === "discovering" ? 2
    : job.status === "discovered" && !research ? 3
    : job.status === "enriching" || research?.status === "running" ? 4
    : research?.status === "completed" ? 5
    : 3;

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/discovery/jobs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJobId(r.data.id);
      setData({ job: r.data, rows: [], counts: {} });
      loadJobs();
      toast.success(`${r.data.row_count} trucks staged — review the cost estimate, then run.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runDiscovery = async (mode) => {
    try {
      const r = await api.post(`/admin/discovery/jobs/${jobId}/run`, { mode });
      toast.success(`Discovery running on ${r.data.running_rows} rows (${mode} mode)…`);
      loadJob(jobId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start discovery");
    }
  };

  const runEnrichment = async (mode) => {
    try {
      const r = await api.post(`/admin/discovery/jobs/${jobId}/enrich`, { mode });
      toast.success(`Enrichment started on ${r.data.row_count} rows — Hunter enabled on ${r.data.hunter_enabled_rows}.`);
      loadJob(jobId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start enrichment");
    }
  };

  const patchRow = async (rowId, patch) => {
    try {
      await api.patch(`/admin/discovery/jobs/${jobId}/rows/${rowId}`, patch);
      loadJob(jobId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const patchResearchRow = async (rowId, patch) => {
    try {
      await api.patch(`/admin/research/jobs/${research.id}/rows/${rowId}`, patch);
      loadJob(jobId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const download = (path, name) => {
    const base = BACKEND_BASE;
    const token = localStorage.getItem("tt_auth_token") || localStorage.getItem("tt_token");
    fetch(`${base}/api/admin/discovery/jobs/${jobId}/${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include",
    }).then(async (r) => {
      if (!r.ok) throw new Error((await r.json()).detail || "export failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = name; a.click();
      URL.revokeObjectURL(a.href);
    }).catch((e) => toast.error(String(e.message || e)));
  };

  const deleteJob = async () => {
    if (!window.confirm("Delete this Expansion job (discovery + linked enrichment rows)?")) return;
    await api.delete(`/admin/discovery/jobs/${jobId}`);
    setJobId(null); setData(null); loadJobs();
  };

  const visibleRows = rows.filter((r) => {
    if (filter === "all") return true;
    if (["High", "Medium", "Low"].includes(filter)) return r.discovery_confidence_label === filter;
    return r.discovery_status === filter;
  });

  const est = job?.estimates || {};

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8 space-y-6" data-testid="expansion-engine-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">Expansion Engine</div>
          <h1 className="font-display font-bold text-2xl">Discovery → Enrichment → Import</h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Find the truck online → verify it → enrich it → import it. High confidence = machine-verified live.
            Hunter.io only runs on High-confidence or rows you approve.
          </p>
        </div>
        {job && (
          <Button variant="ghost" size="sm" onClick={deleteJob} className="text-rose-600" data-testid="expansion-delete-job-btn">
            <Trash className="h-4 w-4" />
          </Button>
        )}
      </header>

      <Stepper current={step} />

      {/* ── Step 1: Upload / job picker ─────────────────────────── */}
      {!job && (
        <section className="rounded-2xl border border-dashed border-border p-8 text-center space-y-3" data-testid="expansion-upload-zone">
          <UploadSimple className="h-8 w-8 mx-auto text-primary" />
          <p className="text-sm">Upload a seed CSV — needs <code>truck_name</code>; <code>city</code>, <code>state</code>, <code>cuisine_type</code>, <code>service_area</code> optional.</p>
          <input ref={fileRef} type="file" accept=".csv" className="hidden"
                 onChange={(e) => onUpload(e.target.files?.[0])} data-testid="expansion-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="expansion-upload-btn">
            {uploading ? "Uploading…" : "Choose Seed CSV"}
          </Button>
          {jobs.length > 0 && (
            <div className="pt-4 text-left max-w-xl mx-auto">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Recent jobs</div>
              {jobs.map((j) => (
                <button key={j.id} onClick={() => setJobId(j.id)}
                        className="w-full flex items-center justify-between text-xs border-b border-border py-1.5 hover:bg-muted/30 px-1"
                        data-testid={`expansion-job-${j.id}`}>
                  <span className="truncate">{j.filename} · {j.row_count} rows</span>
                  <span className="text-muted-foreground">{j.status}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Step 2: Cost estimate + run ─────────────────────────── */}
      {job && job.status === "staged" && (
        <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="expansion-estimate-card">
          <h2 className="font-display font-bold text-lg">{job.filename} — {job.row_count} trucks staged</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["Discovery (GPT-5.4)", `~$${est.discovery_cost_usd}`],
              ["Enrichment (GPT-5.2)", `~$${est.enrichment_cost_usd}`],
              ["Hunter credits (max)", est.hunter_credits_max],
              [`Test run (${est.test_rows} rows)`, `~$${est.test_cost_usd}`],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-border p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
                <div className="font-bold text-lg" data-testid={`estimate-${k.split(" ")[0].toLowerCase()}`}>{v}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Nothing has run yet — no cost incurred. Start with a test batch to sanity-check quality.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => runDiscovery("test")} data-testid="run-discovery-test-btn" className="gap-1">
              <Flask className="h-4 w-4" /> Test on {est.test_rows} rows
            </Button>
            <Button onClick={() => runDiscovery("full")} data-testid="run-discovery-full-btn" className="gap-1">
              <Rocket className="h-4 w-4" /> Run full batch ({job.row_count})
            </Button>
          </div>
        </section>
      )}

      {/* ── Step 2b: Discovery progress ─────────────────────────── */}
      {job && job.status === "discovering" && (
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="expansion-discovery-progress">
          <div className="text-sm font-semibold mb-2">
            Discovering ({job.run_mode}) — {job.discovery_progress}/{job.batch_size}
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${job.discovery_pct || 0}%` }} />
          </div>
        </section>
      )}

      {/* ── Step 3: Discovery review ────────────────────────────── */}
      {job && rows.length > 0 && (
        <section className="space-y-3" data-testid="expansion-discovery-review">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <h2 className="font-display font-bold text-lg">Discovery results ({counts.total})</h2>
            <div className="flex gap-2 flex-wrap">
              {job.status === "discovered" && job.run_mode === "test" && counts.total < job.row_count && (
                <Button size="sm" variant="outline" onClick={() => runDiscovery("full")} data-testid="run-remaining-btn" className="gap-1">
                  <Rocket className="h-3.5 w-3.5" /> Run remaining {job.row_count - counts.total}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => download("discovery.csv", "discovery.csv")} data-testid="dl-discovery-csv" className="gap-1">
                <DownloadSimple className="h-3.5 w-3.5" /> Discovery CSV
              </Button>
              <Button size="sm" variant="outline" onClick={() => download("review.csv", "discovery-review.csv")} data-testid="dl-review-csv" className="gap-1">
                <DownloadSimple className="h-3.5 w-3.5" /> Review CSV (excluded)
              </Button>
              {!research && (
                <>
                  <Button size="sm" variant="outline" onClick={() => runEnrichment("test")} data-testid="run-enrichment-test-btn" className="gap-1">
                    <Flask className="h-3.5 w-3.5" /> Test enrich 5
                  </Button>
                  <Button size="sm" onClick={() => runEnrichment("full")} data-testid="run-enrichment-btn" className="gap-1">
                    Run Enrichment ({counts.enrich_eligible}) <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_CHIPS.map((c) => (
              <button key={c.key} onClick={() => setFilter(c.key)}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${filter === c.key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted/40"}`}
                      data-testid={`filter-${c.key}`}>
                {c.label}{counts[c.key] != null ? ` (${counts[c.key]})` : ""}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-left">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">Decide</th>
                  <th className="px-2 py-2">Truck</th>
                  <th className="px-2 py-2">Website</th>
                  <th className="px-2 py-2">Instagram</th>
                  <th className="px-2 py-2">Facebook</th>
                  <th className="px-2 py-2">Maps / Dir.</th>
                  <th className="px-2 py-2">Conf.</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2 min-w-[220px]">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {visibleRows.map((r) => (
                  <tr key={r.id} className={r.admin_decision === "rejected" ? "opacity-40" : ""} data-testid={`disc-row-${r.id}`}>
                    <td className="px-2 py-2 align-top whitespace-nowrap">
                      <button onClick={() => patchRow(r.id, { admin_decision: r.admin_decision === "accepted" ? "pending" : "accepted" })}
                              className={`p-1 rounded ${r.admin_decision === "accepted" ? "bg-emerald-100 text-emerald-700" : "text-neutral-400 hover:text-emerald-600"}`}
                              title="Accept" data-testid={`accept-${r.id}`}>
                        <Check weight="bold" className="h-4 w-4" />
                      </button>
                      <button onClick={() => patchRow(r.id, { admin_decision: r.admin_decision === "rejected" ? "pending" : "rejected" })}
                              className={`p-1 rounded ${r.admin_decision === "rejected" ? "bg-rose-100 text-rose-700" : "text-neutral-400 hover:text-rose-600"}`}
                              title="Reject" data-testid={`reject-${r.id}`}>
                        <X weight="bold" className="h-4 w-4" />
                      </button>
                      {r.discovery_confidence_label !== "High" && (
                        <label className="flex items-center gap-1 text-[9px] text-muted-foreground mt-1 cursor-pointer" title="Allow Hunter.io on this row">
                          <input type="checkbox" checked={!!r.hunter_approved}
                                 onChange={(e) => patchRow(r.id, { hunter_approved: e.target.checked })}
                                 data-testid={`hunter-approve-${r.id}`} />
                          Hunter
                        </label>
                      )}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <div className="font-semibold">{r.resolved_business_name || r.truck_name}</div>
                      <div className="text-[10px] text-muted-foreground">{r.city}{r.state ? `, ${r.state}` : ""} · {(r.vendor_type || "unknown").replace(/_/g, " ")}</div>
                      {r.visible_email && <div className="text-[10px] text-emerald-700 mt-0.5">✉ {r.visible_email}</div>}
                    </td>
                    <td className="px-2 py-2 align-top"><UrlCell icon={Globe} url={r.website_url} verified={r.website_verified} /></td>
                    <td className="px-2 py-2 align-top"><UrlCell icon={InstagramLogo} url={r.instagram_url} verified={r.instagram_verified} /></td>
                    <td className="px-2 py-2 align-top"><UrlCell icon={FacebookLogo} url={r.facebook_url} verified={r.facebook_verified} /></td>
                    <td className="px-2 py-2 align-top space-y-0.5">
                      <UrlCell icon={MapPin} url={r.google_maps_url} />
                      <UrlCell icon={MagnifyingGlass} url={r.yelp_url || r.streetfoodfinder_url || r.bestfoodtrucks_url} />
                    </td>
                    <td className="px-2 py-2 align-top"><ConfPill label={r.discovery_confidence_label} score={r.discovery_confidence_score} /></td>
                    <td className="px-2 py-2 align-top"><StatusPill s={r.discovery_status} /></td>
                    <td className="px-2 py-2 align-top">
                      <div className="text-[11px] text-muted-foreground leading-snug" title={r.discovery_notes}>{r.reason_for_status}</div>
                      {(r.source_urls || []).length > 0 && (
                        <div className="text-[9px] text-muted-foreground/70 mt-0.5 truncate max-w-[240px]" title={(r.source_urls || []).join("\n")}>
                          {r.source_urls.length} source URL{r.source_urls.length === 1 ? "" : "s"}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Step 4: Enrichment progress ─────────────────────────── */}
      {research && research.status === "running" && (
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="expansion-enrichment-progress">
          <div className="text-sm font-semibold mb-2">Enriching — {research.progress}/{research.row_count}</div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${research.progress_pct || 0}%` }} />
          </div>
        </section>
      )}

      {/* ── Step 5: Final review ────────────────────────────────── */}
      {research && research.status === "completed" && (
        <section className="space-y-3" data-testid="expansion-final-review">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <h2 className="font-display font-bold text-lg">Final results ({researchRows.length})</h2>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => download("import.csv", "trucktrackr-import.csv")} data-testid="dl-import-csv" className="gap-1">
                <DownloadSimple className="h-3.5 w-3.5" /> Import CSV ({counts.import_eligible})
              </Button>
              <Button size="sm" variant="outline" onClick={() => download("outreach.xlsx", "outreach-crm.xlsx")} data-testid="dl-outreach-xlsx" className="gap-1">
                <DownloadSimple className="h-3.5 w-3.5" /> Outreach XLSX
              </Button>
              <Button size="sm" variant="outline" onClick={() => download("review.csv", "discovery-review.csv")} className="gap-1">
                <DownloadSimple className="h-3.5 w-3.5" /> Review CSV
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Import CSV only includes resolved + High-confidence or admin-accepted rows · status=coming_soon · claim_status=unclaimed · P1/P2/P3 auto-set.
          </p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-left">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">Truck</th>
                  <th className="px-2 py-2">Email</th>
                  <th className="px-2 py-2">Hunter alternatives</th>
                  <th className="px-2 py-2">Phone</th>
                  <th className="px-2 py-2">Contact via</th>
                  <th className="px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {researchRows.map((r) => {
                  const alts = [...(r.hunter?.generic || []), ...(r.hunter?.personal || [])].filter((e) => e !== r.email);
                  return (
                    <tr key={r.id} data-testid={`final-row-${r.id}`}>
                      <td className="px-2 py-2 font-semibold">{r.truck_name}
                        <div className="text-[10px] text-muted-foreground font-normal">{r.city}</div>
                      </td>
                      <td className="px-2 py-2">{r.email || <span className="text-neutral-300">—</span>}</td>
                      <td className="px-2 py-2">
                        {alts.length > 0 ? (
                          <select className="text-xs bg-transparent border border-border rounded px-1 py-0.5"
                                  defaultValue="" data-testid={`hunter-alt-${r.id}`}
                                  onChange={(e) => e.target.value && patchResearchRow(r.id, { email: e.target.value })}>
                            <option value="">use instead…</option>
                            {alts.map((e) => <option key={e} value={e}>{e}</option>)}
                          </select>
                        ) : <span className="text-neutral-300 text-xs">—</span>}
                      </td>
                      <td className="px-2 py-2 text-xs">{r.phone || "—"}</td>
                      <td className="px-2 py-2 text-xs">{r.best_contact_method || "—"}</td>
                      <td className="px-2 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">{r.research_status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
